import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Define the Pallet Store
interface PalletStore {
  pallets: palletType[]; // Array of pallet objects
  palletTypes: any; // Array of pallet types
  addPallet: (pallet: palletType) => void; // Function to add a pallet
  addPalletType: (palletType: { id: string; value: string }) => void; // Function to add a pallet type
  updatePalletType: (id: string, newPalletTypeValue: string) => void; // Function to update a pallet type by id
  removePallet: (palletName: string) => void; // Function to remove a pallet by name
  activePallet: any | null; // Active pallet (can be null)
  setActivePallet: (palletName: { id: string; value: string }) => void; // Function to set active pallet
  clearPallets: () => void; // Function to clear all pallets
  setPallets:(pallets:palletType[])=>void
}

// Define the structure for each pallet
interface palletType {
  id?: number;
  palletId?: string | undefined;
  variant: string; // Refers to the value of palletType, not id
  name: string;
  shippingMode: string;
  numberOfPallets: number;
  shippingModeId: number;
  updatedAt: string;
  createdAt: string;
}

export const usePalletStore = create<PalletStore>()(
  persist(
    (set) => ({
      pallets: [], // Initial empty array for pallets
      palletTypes: [], // Initial array with an example pallet type
      activePallet: null, // Initialize activePallet as null

      // Add a new pallet to the array
      addPallet: (pallet: palletType) =>
        set((state) => ({
          pallets: [...state.pallets, pallet], // Add the new pallet to the array
        })),
        setPallets: (pallets:any) =>
          set((state) => ({
            palletTypes:pallets, 
          })),
      addPalletType: (palletType: { id: string; value: string }) =>
        set((state) => ({
          palletTypes: [...state.palletTypes, palletType], // Add the new pallet type to the array
        })),

      // Update an existing pallet type's value by its id
      updatePalletType: (id: string | null, newPalletTypeValue: string) =>
        set((state) => {
          // Find the index of the pallet type by its id
          const palletIndex = state.palletTypes.findIndex(
            (type:any) => type.id === id
          );

          if (palletIndex === -1) {
            console.warn("Pallet type with the given ID not found");
            return state; // Return the current state without making changes
          }

          // Proceed to update the pallet type value by id
          const updatedPalletTypes = [...state.palletTypes];
          updatedPalletTypes[palletIndex] = {
            ...updatedPalletTypes[palletIndex],
            value: newPalletTypeValue, // Update only the value field
          };

          return {
            palletTypes: updatedPalletTypes, // Return the updated pallet types array
          };
        }),

      // Set the active pallet by pallet name
      setActivePallet: (palletName: { id: string; value: string }) =>
        set(() => ({
          activePallet: palletName,
        })),

      // Remove a pallet from the array by its name
      removePallet: (palletName: string) =>
        set((state) => ({
          pallets: state.pallets.filter((pallet) => pallet.name !== palletName),
        })),

      // Clear all pallets and reset activePallet
      clearPallets: () =>
        set(() => ({
          pallets: [],
          activePallet: null, // Reset activePallet when clearing
          palletTypes: [],
        })),
    }),
    {
      name: "pallet", // Key for sessionStorage
      storage: createJSONStorage(() => sessionStorage), // Use sessionStorage instead of localStorage
    }
  )
);
